import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

import { CDNAssets } from '../../CDN';
import { ContactUsMode } from '../../components/forms/ContactForm';
import AlternateLayoutList from '../../components/layout/lists/AlternateLayoutList';
import ArchitectureSection from '../../components/layout/sections/ArchitectureSection';
import BuildAgentCTASection from '../../components/layout/sections/BuildAgentCTASection';
import HeaderSection, { HeaderSectionDestinationUrl } from '../../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../../components/layout/sections/PageSection';
import PageSectionTitle from '../../components/layout/sections/PageSectionTitle';
import { withGlobalDefaultStaticProps } from '../../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../../lib/seo/pageSeoConfigs';
import pageRoutes from '../../router/pageRoutes';
import { NextPageWithLayout } from '../../types/global-types';
import StaticScrollingLogosWidget from '../../widgets/animated/StaticScrollingLogosWidget';
import ContactUsWidget from '../../widgets/interactive/ContactUsWidget';
import FAQWidget from '../../widgets/interactive/FAQWidget';
import type { FAQItem } from '../../widgets/interactive/FAQWidget';

export const getStaticProps: GetStaticProps = async (context) => {
  return withGlobalDefaultStaticProps(context, { props: {} });
};

const MARITIME_FAQS: FAQItem[] = [
  {
    question: 'Does Gaspar AI understand maritime terminology like charterparty clauses, SOFs, and ISM?',
    answer:
      "Yes. Gaspar AI Agents are built on a Knowledge Graph that models maritime concepts — vessels, charterers, clauses, vendors — and the relationships between them. The Demurrage Agent interprets charterparty terms and parses Statements of Facts against fixture language. The ISM Agent is trained on the ISM Code and operates against your existing manuals and inspection data. Domain vocabulary is not an afterthought; it's how the system is organized.",
  },
  {
    question: 'How accurate are Gaspar AI Agents?',
    answer:
      "Gaspar AI Agents reach 98%+ accuracy on structured and unstructured maritime data — validated in production at AVIN, Alcyon Shipping, and other operators. Accuracy comes from the hybrid architecture: the Knowledge Graph grounds the agent in the relationships that actually matter in your domain, and Retrieval-Augmented Generation ties every answer back to real source documents rather than the model's training data. For high-stakes workflows, configurable Human-in-the-Loop supervision adds a validation layer before output leaves your environment.",
  },
  {
    question: 'Which systems does Gaspar AI integrate with?',
    answer:
      'Gaspar AI ships with 33+ system connectors spanning ERPs, vessel logs, SharePoint, email, databases, and APIs. Named maritime and enterprise integrations include DocMap, Domino, and Benefit Software, alongside standard systems of record. More broadly, Gaspar AI integrates with any SaaS tool that exposes an API — custom connectors are built on a 2-4 week timeline. Integration is bidirectional: the agent reads from your systems of record and writes results (extracted clauses, claims, reorder requests) back to them.',
  },
  {
    question: 'Where does our data live? Can we deploy on-premises?',
    answer:
      "You choose. Gaspar AI can be deployed on your own cloud, on-premises, or in Gaspar's managed environment. Your data never leaves the environment you designate. Greek and EU operators typically deploy in EU regions; Singaporean and Middle Eastern customers deploy in APAC. The architecture is LLM-agnostic — you can also bring your own model, or use open-source models, to meet data residency requirements.",
  },
  {
    question: 'Can we start with one agent and add more later?',
    answer:
      'Yes. Most maritime customers start with one or two agents — typically Crew Management or Purchasing, which deliver measurable impact on day-one operations — validate accuracy in production, then add more agents as trust is established. Because all five agents share the same Knowledge Graph, adding an agent does not require re-deploying the underlying platform. Agents can also graduate from Human-in-the-Loop to Human-on-the-Loop supervision workflow by workflow as your team builds confidence.',
  },
  {
    question: 'How is Gaspar AI different from a general-purpose AI tool like ChatGPT or a knowledge management platform?',
    answer:
      "General-purpose AI tools are not grounded in your operational systems and will hallucinate on specialized maritime documents. Knowledge management platforms can surface documents but cannot act — they don't calculate laytime, draft claim letters, or forecast vendor reorders. Gaspar AI is a hybrid of Retrieval-Augmented Generation and a Knowledge Graph specifically modeled for maritime workflows, with agents that take action inside your existing systems rather than sitting alongside them.",
  },
  {
    question: 'What if we need an agent for a workflow that is not one of the five?',
    answer:
      "Gaspar AI's framework is designed to scale to new agents. Common custom agents include vetting, bunkering, port agency, crew welfare, and voyage planning. If the workflow lives in your documents and systems, an agent can be built around it. Talk to our maritime team about the specific workflow you want to automate.",
  },
];

const MARITIME_LOGOS = [
  { src: '/icons/maritime/alcyon.svg', alt: 'Alcyon' },
  { src: '/icons/maritime/avin.svg', alt: 'Avin' },
  { src: '/icons/maritime/tms.svg', alt: 'TMS' },
];

const MaritimePage: NextPageWithLayout = () => {
  // TODO: add pageRoutes.IndustriesMaritime to src/router/pageRoutes
  // TODO: add corresponding entry to src/lib/seo/pageSeoConfigs
  const seoConfig = getPageSeoConfig(pageRoutes.WorkAssistantForMaritime);

  return (
    <>
      <NextSeo {...seoConfig} />

      <HeaderSection
        subTitle="Gaspar AI Agents for Maritime"
        title="Specialized AI agents for maritime operations"
        description={
          <>
            <p className="max-w-[800px] text-2xl">
              Crew management, purchasing, ISM compliance, demurrage, and vessel manuals — built on a hybrid Vector + Knowledge Graph
              architecture.
            </p>
            <p className="text-4xl font-bold mt-4">98%+ accuracy of our specialied agents build for our customers</p>
          </>
        }
        destinationUrl={HeaderSectionDestinationUrl.RequestDemo}
      />

      <PageSection isNarrow={true}>
        <StaticScrollingLogosWidget logos={MARITIME_LOGOS} logoHeight={80} />
      </PageSection>

      <PageSection type={PageSectionType.White}>
        <PageSectionTitle text="Five specialized agents, built for how you actually operate" />
        <AlternateLayoutList
          items={[
            {
              imageUrl: CDNAssets.getUrl('for-maritime-page-crew-management'),
              orientation: 'left',
              title: 'Crew Management Agent',
              description:
                'Real-time visibility into crew positions, contracts, and certificates. Proactive alerts on expiring documents and compliance issues. Reduces administrative time and keeps planning ahead of the problem rather than behind it.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-maritime-page-purchasing'),
              orientation: 'right',
              title: 'Purchasing Agent',
              description:
                'Analyzes purchase trends and vendor reliability. Automates reorders and forecasts future needs across your fleet. Cuts procurement costs and surfaces spend patterns your team can act on.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-maritime-page-ism'),
              orientation: 'left',
              title: 'ISM Agent',
              description:
                'Ingests your manuals, reports, and inspection data. Tracks non-conformities, inspections, and audits. Answers ISM queries instantly and generates compliance reports — grounded in your own documents, not a generic model.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-maritime-page-demurrage'),
              orientation: 'right',
              title: 'Demurrage Agent',
              description:
                'Reads vessel logs, interprets charterparty terms, calculates laytime automatically. Detects delays early and drafts claim letters your team can review and send — turning a multi-day paperwork cycle into a same-day workflow.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-maritime-page-technical-reference'),
              orientation: 'left',
              title: 'Technical Reference Agent',
              description:
                'Reads your vessel manuals, maintenance logs, and historical incident reports. When a mechanical problem surfaces, the agent cross-references the knowledge graph to surface the fixes that worked last time — on this vessel, on a sister ship, or across the fleet.',
            },
          ]}
        />
      </PageSection>

      <ArchitectureSection />

      <PageSection>
        <p className="text-sm font-medium uppercase tracking-[0.2em] text-purple mb-4">Credentialed in production</p>
        <h2 className="font-medium max-w-3xl mb-12">Trusted by leading maritime operators.</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div>
            <p className="text-5xl font-medium text-purple leading-none">98%+</p>
            <p className="text-pureWhite/70 mt-3 leading-snug">Accuracy on structured &amp; unstructured maritime data</p>
          </div>
          <div>
            <p className="text-5xl font-medium text-purple leading-none">33+</p>
            <p className="text-pureWhite/70 mt-3 leading-snug">System connectors — ERPs, vessel logs, SharePoint, emails, APIs</p>
          </div>
          <div>
            <p className="text-5xl font-medium text-purple leading-none">Any mode</p>
            <p className="text-pureWhite/70 mt-3 leading-snug">Configurable supervision — HITL or HOTL per workflow</p>
          </div>
          <div>
            <p className="text-5xl font-medium text-purple leading-none">Flexible</p>
            <p className="text-pureWhite/70 mt-3 leading-snug">
              Deployment — on your cloud, on-premises, your data never leaves your environment
            </p>
          </div>
        </div>
        <p className="text-pureWhite/70 max-w-3xl mt-8 leading-relaxed">
          LLM-agnostic architecture means you&apos;re never locked into a single provider. Swap models without re-architecting — and keep
          your domain knowledge graph intact.
        </p>
      </PageSection>

      <BuildAgentCTASection />

      <PageSection type={PageSectionType.White}>
        <PageSectionTitle text="Why this is hard without the right architecture" />
        <AlternateLayoutList
          items={[
            {
              imageUrl: CDNAssets.getUrl('for-maritime-page-anchor'),
              orientation: 'left',
              title: 'Email, Excel, and legacy tools dominate',
              description:
                'Maritime operations still run on inboxes and spreadsheets. Crew management, purchasing, ISM, and demurrage are manual, disconnected, and time-consuming — and generic LLMs make them worse by hallucinating on domain documents.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-maritime-page-waves'),
              orientation: 'right',
              title: 'Compliance and cost pressures only grow',
              description:
                'SIRE 2.0, tightening regulations, and operator scrutiny raise the compliance bar every year. At the same time, OPEX pressure is relentless. You need agents that operate inside your systems — not yet another tool your team has to feed.',
            },
          ]}
        />
      </PageSection>

      <PageSection type={PageSectionType.Purple}>
        <FAQWidget items={MARITIME_FAQS} />
      </PageSection>

      <PageSection>
        <ContactUsWidget defaultMode={ContactUsMode.DemoRequest} showAllModes={false} />
      </PageSection>
    </>
  );
};

MaritimePage.displayName = 'MaritimePage';
export default MaritimePage;
